import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "~hooks/useNotification";
import { useApi } from "../../../shared/api";
import type { IUniformUpsertModel } from "../models/IUniformUpsertModel";
import { useUpsertUniformItem } from "./useUpsertUniformItem";

export const useUpsertUniform = () => {
  const { sport, organizationId } = useParams();
  const { mutateData } = useApi();
  const queryClient = useQueryClient();
  const { mutateAsync: upsertUniformItem } = useUpsertUniformItem();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: (upsertModel: IUniformUpsertModel) => {
      let url = `${sport}/o/${organizationId}/uniforms`;
      if (upsertModel.uniformId) {
        url = `${url}/${upsertModel.uniformId}`;
      }
      return mutateData({
        url: url,
        method: upsertModel.uniformId ? "PUT" : "POST",
        data: upsertModel.upsertData,
      });
    },
    onSuccess: async (response: any, upsertModel: IUniformUpsertModel) => {
      const uniformId = response?.data?.[0]?.uniformId;
      if (uniformId) {
        for (let i = 0; i < upsertModel.items?.length; i++) {
          const { uniformItemId, ...uniformItemUpsertData } = upsertModel.items[i];
          await upsertUniformItem({
            uniformId,
            uniformItemId,
            upsertData: uniformItemUpsertData,
          });
        }

        await queryClient.invalidateQueries({
          queryKey: ["uniformItems", sport, organizationId, uniformId],
        });

        await queryClient.invalidateQueries({
          queryKey: ["uniforms", sport, organizationId, upsertModel.upsertData.baseType, upsertModel.upsertData.baseId],
        });

        notification.success({
          message: formatMessage({ id: upsertModel.uniformId ? "uniform.update.success" : "uniform.add.success" }),
          placement: "bottomRight",
        });
      }
    },
  });
};
